import React, { useContext } from "react";
import { skillGroups } from "../constants";
import { ThemeContext } from "../themeProvider";
import { motion } from "framer-motion";

const quickFacts = [
  { label: "Based in", value: "Turku, Finland" },
  { label: "Working as", value: "AI Engineer at UESTCO" },
  { label: "Building", value: "Zephio, as Co-Founder" },
  { label: "Studying", value: "Data Science, University of Turku" },
  { label: "Researching", value: "Quantum-native language models" },
  { label: "Background", value: "Computer engineering, XR and commercial software" },
];

const interests = [
  "Rapping",
  "Camping",
  "Music",
  "Longboarding",
  "Rollerblading",
  "Drawing",
  "XR experiments",
  "Digital nomad life",
];

const About = () => {
  const theme = useContext(ThemeContext);
  const darkMode = theme.state.darkMode;
  const panelClassName = darkMode
    ? "border border-gray-200 bg-white text-gray-900"
    : "border border-white border-opacity-20 bg-black bg-opacity-40 text-white";
  const mutedTextClassName = darkMode ? "text-gray-500" : "text-gray-300";
  const chipClassName = darkMode
    ? "px-4 py-2 rounded-lg bg-gray-100 text-gray-900 font-semibold"
    : "px-4 py-2 rounded-lg bg-white bg-opacity-10 text-white font-semibold border border-white border-opacity-20";
  const chipVariants = {
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
      },
    },
    hidden: { opacity: 1, y: 30 },
  };

  return (
    <div id="about"
    // className={darkMode === true ? "bg-white" : "bg-gray-900"}
    >
      <div className="max-w-7xl mx-auto x-4 sm:px-6 lg:px-8 px-4 md:mt-0 pt-24 pb-12">
        <div style={{ width: "100%", alignItems: "center", justifyContent: "center", alignContent: "center", display: "flex" }}>
          <h2
            className={
              darkMode
                ? "text-5xl font-bold px-4 md:px-0 text-center colored-bottom-line"
                : "text-5xl font-bold px-4 md:px-0 text-center text-white colored-bottom-line"
            }
          >
            About Me
          </h2>
        </div>
        <div>
          <motion.div>
            <h4 className="mt-12 text-3xl font-semibold multicolortext" style={{ width: "max-content", maxWidth: "100%" }}>
              Short version
            </h4>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {quickFacts.map((item) => (
                <motion.div
                  key={item.label}
                  initial="hidden"
                  whileInView={"visible"}
                  variants={{
                    visible: {
                      y: 0,
                      opacity: 1,
                      transition: {
                        type: "spring",
                      },
                    },
                    hidden: { opacity: 1, y: 50 },
                  }}
                  className={`p-5 rounded-lg ${panelClassName}`}
                >
                  <p className={`text-sm uppercase font-semibold ${mutedTextClassName}`}>{item.label}</p>
                  <p className="mt-2 text-2xl md:text-3xl font-bold">{item.value}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div>
            <h4 className="mt-12 text-3xl font-semibold multicolortext" style={{ width: "max-content", maxWidth: "100%" }}>
              Longer version
            </h4>
            <p className={`mt-6 text-xl ${darkMode ? "text-gray-500" : "text-white"}`}>
              I studied computer engineering in Istanbul and spent the years around it building things for other people: XR applications in Unity at Kuvars Tech, desktop and hardware software at Monster Notebook, client projects at CoreXas, and a long list of expo installations for brands like Coca-Cola, IQOS and Heineken, the kind of software that can't crash in front of a crowd.
              <br /><br />
              Somewhere along the way the most interesting problems moved to AI, and so did I. I now work as an AI engineer at UESTCO on computer vision for continuous camera feeds, and I'm doing the EIT Digital double-degree master's: first year at ELTE in Budapest, second year at the University of Turku in Finland, where I live now.
              <br /><br />
              I like ideas that sound slightly unreasonable until you build them: a browser for an internet that doesn't exist, a ranking model for your own memories, a language model whose memory lives in a quantum register. Zephio, the company I co-founded, is where that building turns into work for clients, and where I try to turn research ideas into products people can actually use.
            </p>
          </motion.div>
          <motion.div>
            <h4 className="mt-12 text-3xl font-semibold multicolortext" style={{ width: "max-content", maxWidth: "100%" }}>
              Technologies and Tools
            </h4>
            <div className="mt-6 space-y-6">
              {skillGroups.map((group) => (
                <div key={group.name}>
                  <p className={`text-sm uppercase font-semibold ${mutedTextClassName}`}>{group.name}</p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {group.skills.map((skill) => (
                      <motion.span
                        key={skill}
                        initial="hidden"
                        whileInView={"visible"}
                        variants={chipVariants}
                        className={chipClassName}
                      >
                        {skill}
                      </motion.span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div
          >
            <h4 className="mt-12 text-3xl font-semibold multicolortext" style={{ width: "max-content", maxWidth: "100%" }}>
              Also Deniz
            </h4>
            <div className="mt-6 flex flex-wrap gap-3">
              {interests.map((interest) => (
                <motion.span
                  key={interest}
                  initial="hidden"
                  whileInView={"visible"}
                  variants={chipVariants}
                  className={chipClassName}
                >
                  {interest}
                </motion.span>
              ))}
            </div>
            <p
              className={`mt-8 text-xl ${darkMode ? "text-gray-500" : "text-white"}`}
              >
              Developer, performer, maker. I like work that feels alive.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default About;
